import { FC, ReactNode } from 'react'
import { CardComponent } from '@BidftaStrapi/Components/types'
import { CardElement } from '@BidftaStrapi/Elements/types'
import Link from 'next/link'
import classNames from 'classnames'
import Image from 'next/image'
import { useIsMobile } from '@BidftaStrapi/utils/functions'

type Props = CardComponent & {
  span: number
  href?: string
  hasNoExtraPadding?: boolean
  minHeight?: number // desktop min-height (rem)
  mobileMinHeight?: number // mobile min-height (rem)
  tabletMinHeight?: number // tablet min-height (rem)
  /**
   * OPTIONAL: CSS aspect-ratio for image cards (e.g. "16/9", "4/3").
   * If you pass this, the card becomes a “hero” and will get extra top-margin.
   * Otherwise it falls back to your minHeight logic.
   */
  aspectRatio?: string
  children: ReactNode
}

const RowCard: FC<Props> = ({
  type,
  color,
  gradient,
  image,
  position,
  edges,
  mobileType,
  mobileColor,
  mobileGradient,
  mobileImage,
  mobileEdges,
  span,
  href,
  hasNoExtraPadding,
  minHeight = 20,
  mobileMinHeight = 12,
  tabletMinHeight,
  aspectRatio,
  children,
}) => {
  const isMobile = useIsMobile()

  // pick desktop vs mobile values
  const bg: CardElement = {
    type: isMobile ? mobileType || type : type,
    color: isMobile ? mobileColor! : color!,
    gradient: isMobile ? mobileGradient! : gradient!,
    image: isMobile ? mobileImage || image : image!,
    edges: isMobile ? mobileEdges! : edges!,
    position,
  }

  // only used for non-image cards
  const desiredMinHeight =
    bg.type === 'image'
      ? 0
      : isMobile
      ? mobileMinHeight
      : tabletMinHeight ?? minHeight

  // do we treat this as the “hero”?
  const isHero = bg.type === 'image' && Boolean(aspectRatio)

  // build inline style
  const style: React.CSSProperties = {}
  if (bg.type === 'image') {
    if (isHero) {
      style.aspectRatio = aspectRatio!
      style.width = '100%'
    } else {
      style.minHeight = `${desiredMinHeight}rem`
    }
  } else {
    Object.assign(style, getBackground(bg))
    style.minHeight = `${desiredMinHeight}rem`
  }

  return (
    <Wrapper
      href={href}
      span={span}
      hasNoExtraPadding={hasNoExtraPadding}
      isHero={isHero}
    >
      <div
        className={classNames(
          'relative w-full overflow-hidden',
          bg.edges === 'flat' ? '' : 'rounded-xl'
        )}
        style={style}
      >
        {bg.type === 'image' && bg.image?.url && (
          <Image
            src={bg.image.url}
            alt="Card image"
            width={16}
            height={9}
            className="object-cover w-full h-auto"
            sizes="(max-width: 768px) 100vw, 100vw"
            priority={false}
          />
        )}
        <div className="absolute inset-0 z-10 flex flex-col">{children}</div>
      </div>
    </Wrapper>
  )
}

type WrapperProps = {
  children: ReactNode
  href?: string
  span: number
  hasNoExtraPadding?: boolean
  isHero?: boolean
}

const Wrapper: FC<WrapperProps> = ({
  children,
  href,
  span,
  hasNoExtraPadding,
  isHero,
}) => {
  // If it's a hero card, give it extra top-margin so it clears the carousel dots/arrows
  const margin = hasNoExtraPadding ? 'mt-1' : isHero ? 'mt-4 mb-1' : 'my-1'

  const classes = classNames(
    'block',
    `col-span-${span}`,
    `md:col-span-${span}`,
    margin,
    href ? 'hover:scale-105 ease-in-out duration-200' : ''
  )

  return href ? (
    <Link href={href} className={classes}>
      {children}
    </Link>
  ) : (
    <div className={classes}>{children}</div>
  )
}

// Compute CSS backgrounds for solid/gradient cards
const getBackground = (background: CardElement) => {
  const {
    type = 'solid',
    color = '#FFFFFF',
    gradient: { start, end, degree } = {
      start: '#0000ff',
      end: '#ff00ff',
      degree: 90,
    },
  } = background

  switch (type) {
    case 'gradient':
      return { background: `linear-gradient(${degree}deg, ${start}, ${end})` }
    case 'image':
      // fallback if image fails
      return { backgroundColor: color }
    default:
      return { backgroundColor: color }
  }
}

export default RowCard
